import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { AI_CREDITS_USED_UP_MESSAGE } from "@repo/app-types/helpers/billing/aiCredits";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC, ReactNode } from "react";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";

interface AiCreditsDisabledTooltipProps {
  isCreditsExhausted: boolean;
  /** The control to explain, rendered disabled while credits are used up. */
  children: ReactNode;
  className?: string;
}

/** Why a credit-using control is disabled, worded for the teammate's role. */
export function useAiCreditsDisabledReason(): string {
  const authorization = useTeammateAuthorization();
  return authorization.can(PermissionActions.UPDATE, PermissionSubjects.BILLING)
    ? `${AI_CREDITS_USED_UP_MESSAGE}. Contact us to add credits.`
    : `${AI_CREDITS_USED_UP_MESSAGE}. Contact your billing admin to add credits.`;
}

/** Explains a control disabled because the company's AI credits are used up. */
export const AiCreditsDisabledTooltip: FC<AiCreditsDisabledTooltipProps> = (
  props,
) => {
  const { isCreditsExhausted, children, className } = props;
  const reason = useAiCreditsDisabledReason();
  if (!isCreditsExhausted) {
    return children;
  }

  return (
    <TooltipTrigger>
      <TooltipHoverTarget shape="rounded" className={className}>
        {children}
      </TooltipHoverTarget>
      <SimpleTooltip>{reason}</SimpleTooltip>
    </TooltipTrigger>
  );
};
